import * as React from "react";
import { GatsbyImage } from "gatsby-plugin-image";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import Link from "./link";
import RichText from "./rich-text";
import ImageWithText from "./image-with-text";
import Slideshow from "./slideshow";
import Typeform from "./typeform";
import Embed from "./embed";
import ButtonLink from "./button-link";
import ZiteEmbed from "./zite-embed";

const renderOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => {
      const [{ value }] = node.content;

      if (value !== "" || node.content.length > 1) {
        return <p>{children}</p>;
      }

      return null;
    },

    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const image = node.data.target;

      if (image) {
        return <GatsbyImage image={image.gatsbyImageData} alt={image.title} />;
      }

      return null;
    },

    [INLINES.HYPERLINK]: (node, children) => (
      <Link href={node.data.uri}>{children}</Link>
    ),

    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const entry = node.data.target;

      if (entry?.__typename === "ContentfulLink") {
        return (
          <ButtonLink
            href={entry.link}
            button={entry.button}
            target={entry.target}
            backgroundColor={entry.buttonBackgroundColour}
            textColor={entry.textColour}
            modal={entry.modal}
          >
            {entry.title}
          </ButtonLink>
        );
      }

      if (entry?.__typename === "ContentfulAssetOriginalRatio") {
        const imgWidth = entry.image.file.details.image.width;
        const imgHeight = entry.image.file.details.image.height;

        return (
          <div style={{ width: imgWidth, height: imgHeight, margin: "auto" }}>
            <GatsbyImage
              image={entry.image.gatsbyImageData}
              alt={entry.title}
            />
          </div>
        );
      }

      if (entry?.__typename === "ContentfulImageWithText") {
        return (
          <ImageWithText
            image={entry.image}
            mobileImage={entry.mobileImage}
            imageAlt={entry.imageAlt}
            text={entry.text}
            link={entry.link}
            modal={entry.modal}
          />
        );
      }

      return null;
    },
  },

  renderText: (text) => {
    return text
      .split("\n")
      .flatMap((text, index) => [index > 0 && <br key={index} />, text]);
  },
};

const Block = ({ block }) => {
  switch (block.__typename) {
    case "ContentfulRichText":
      return (
        <RichText>
          {block.content && renderRichText(block.content, renderOptions)}
        </RichText>
      );

    case "ContentfulImageWithText":
      return (
        <ImageWithText
          image={block.image}
          mobileImage={block.mobileImage}
          imageAlt={block.imageAlt}
          text={block.text}
          link={block.link}
          modal={block.modal}
        />
      );

    case "ContentfulSlideshow":
      return (
        <Slideshow
          slides={block.slides}
          fullWidth={block.layout === "Pleine largeur"}
          backgroundColor={block.backgroundColor}
          noPadding={block.padding === false}
        />
      );

    case "ContentfulTypeform":
      return <Typeform form={block.form} />;

    case "ContentfulEmbed":
      return <Embed url={block.url} />;

    case "ContentfulZiteEmbed":
      return <ZiteEmbed id={block.ziteId} />;

    default:
      return null;
  }
};

export default Block;
